import React, { useState } from 'react';
import { Avatar } from '../ui/avatar';
import { Check, CheckCheck, Heart, Reply, MoreHorizontal, Copy, Forward, Trash2, Smile, Clock3 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { AnimatePresence, motion } from 'framer-motion';
import { MediaPreview } from '../ui/media-preview';
import { LinkPreview } from '../ui/link-preview';
import { MessageReactions } from '../ui/message-reactions';
import { Button } from '../ui/button';
import { MessageReactionPicker } from './message-reaction-picker';

interface Message {
  id: string;
  content: string;
  senderId: string;
  receiverId?: string;
  participants?: string[];
  senderName?: string;
  senderPhoto?: string;
  createdAt: Date;
  seen: boolean;
  deliveredAt?: Date | null;
  deliveredTo?: Record<string, Date>;
  seenBy?: Record<string, Date>;
  readAt?: string | null;
  mediaUrl?: string;
  mediaType?: string;
  type?: string;
  isEdited?: boolean;
  optimisticStatus?: 'sending' | 'sent';
  ephemeral?: boolean;
  expiresAt?: Date | null;
}

interface MessageBubbleProps {
  message: Message;
  isSender: boolean;
  onReply?: (message: Message) => void;
  onDelete?: (messageId: string) => void;
  onEdit?: (message: Message) => void;
  onForward?: (message: Message) => void;
}

export function MessageBubble({ message, isSender, onReply, onDelete, onEdit, onForward }: MessageBubbleProps) {
  const [showActions, setShowActions] = useState(false);
  
  const formatMessageDate = (date: Date) => {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
      return 'Just now';
    }
    return formatDistanceToNow(date, { addSuffix: true });
  };

  // Detect URLs in message content
  const detectLinks = (text: string) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    return text.match(urlRegex) || [];
  };

  const copyMessage = () => {
    navigator.clipboard.writeText(message.content);
    setShowActions(false);
  };

  const handleReply = () => {
    onReply?.(message);
    setShowActions(false);
  };

  const handleDelete = () => {
    if (isSender && onDelete) {
      onDelete(message.id);
    }
    setShowActions(false);
  };

  const handleEdit = () => {
    if (isSender && onEdit) {
      onEdit(message);
    }
    setShowActions(false);
  };

  const handleForward = () => {
    onForward?.(message);
    setShowActions(false);
  };

  const links = message.content ? detectLinks(message.content) : [];
  const isEphemeral = Boolean(message.ephemeral) || message.id.startsWith('local-ephemeral-');

  const isEmojiOnlyMessage = (text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return false;

    const withoutEmoji = trimmed
      .replace(/[\p{Emoji_Presentation}\p{Extended_Pictographic}\uFE0F]/gu, '')
      .replace(/\s/g, '');

    return withoutEmoji.length === 0 && [...trimmed].length <= 12;
  };

  const emojiOnly = Boolean(message.content) && isEmojiOnlyMessage(message.content) && !message.mediaUrl;

  const getDeliveryState = (): 'sending' | 'sent' | 'delivered' | 'seen' => {
    if (message.optimisticStatus === 'sending') return 'sending';
    if (message.seen) return 'seen';
    if (message.deliveredAt) return 'delivered';
    if (message.optimisticStatus === 'sent') return 'sent';
    return 'sent';
  };

  const deliveryState = getDeliveryState();

  const getSeenBySummary = (): { label: string; title: string } | null => {
    if (!isSender) return null;
    if ((message.participants?.length || 0) <= 2) return null;

    const entries = Object.entries(message.seenBy || {}).filter(([uid]) => uid !== message.senderId);
    if (entries.length === 0) return null;

    const formatted = entries
      .map(([uid, seenAt]) => {
        const time = seenAt instanceof Date && Number.isFinite(seenAt.getTime())
          ? formatDistanceToNow(seenAt, { addSuffix: true })
          : 'unknown time';
        return `${uid}: ${time}`;
      })
      .sort();

    const label =
      entries.length === 1
        ? `Seen by 1 participant`
        : `Seen by ${entries.length} participants`;

    return {
      label,
      title: formatted.join('\n'),
    };
  };

  const seenBySummary = getSeenBySummary();

  const renderDeliveryIcon = () => {
    if (deliveryState === 'sending') {
      return <Clock3 className="w-4 h-4 text-gray-400" aria-label="Sending" />;
    }

    if (deliveryState === 'sent') {
      return <Check className="w-4 h-4 text-gray-400" aria-label="Sent" />;
    }

    if (deliveryState === 'delivered') {
      return <CheckCheck className="w-4 h-4 text-gray-400" aria-label="Delivered" />;
    }

    return <CheckCheck className="w-4 h-4 text-blue-400" aria-label="Seen" />;
  };

  const renderDeliveryText = () => {
    if (deliveryState === 'sending') return 'Sending...';
    if (deliveryState === 'sent') return 'Sent';
    if (deliveryState === 'delivered') return 'Delivered';
    return 'Seen';
  };

  return (
    <div className={`flex items-end gap-3 mb-6 group ${isSender ? 'flex-row-reverse' : ''}`}>
      {/* Avatar */}
      {!isSender && (
        <div className="flex-shrink-0">
          <Avatar>
            <img
              src={message.senderPhoto || `https://api.dicebear.com/7.x/initials/svg?seed=${message.senderId}`}
              alt={message.senderName || 'User'}
              className="w-10 h-10 rounded-full object-cover"
            />
          </Avatar>
        </div>
      )}

      {/* Message Content */}
      <div className={`flex flex-col max-w-[70%] min-w-0 ${isSender ? 'items-end' : 'items-start'}`}>
        {/* Message Bubble */}
        <div
          className={`relative rounded-2xl px-4 py-3 shadow-sm transition-all duration-200 overflow-hidden ${
            isSender
              ? 'bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-br-md'
              : 'bg-gradient-to-br from-blue-400 to-blue-500 text-white rounded-bl-md'
          }`}
        >
          {/* Message text */}
          {message.content && (
            <p className={`${emojiOnly ? 'text-4xl leading-tight' : 'text-sm leading-relaxed'} !text-white whitespace-pre-wrap break-words break-all overflow-wrap-anywhere`}>
              {message.content}
            </p>
          )}
          
          {/* Media content */}
          {message.mediaUrl && message.mediaType && (
            <div className={message.content ? 'mt-3' : ''}>
              <MediaPreview url={message.mediaUrl} type={message.mediaType} />
            </div>
          )}

          {/* Message tail */}
          <div 
            className={`absolute bottom-0 w-3 h-3 ${
              isSender 
                ? 'right-0 bg-gradient-to-br from-blue-500 to-blue-600' 
                : 'left-0 bg-gradient-to-br from-blue-400 to-blue-500'
            }`}
            style={{
              clipPath: isSender 
                ? 'polygon(0 0, 100% 0, 100% 100%)' 
                : 'polygon(0 100%, 100% 0, 100% 100%)'
            }}
          />
        </div>

        {/* Link Previews */}
        {links.length > 0 && (
          <div className="mt-2 space-y-2 w-full">
            {links.map((link, index) => (
              <LinkPreview key={index} url={link} />
            ))}
          </div>
        )}

        {/* Message Reactions */}
        {!isEphemeral ? (
          <div className="mt-1">
            <MessageReactions messageId={message.id} />
          </div>
        ) : (
          <div className="mt-1 px-1">
            <span className="text-[11px] text-amber-600 inline-flex items-center gap-1">
              <Clock3 className="w-3 h-3" />
              Disappearing message (local-only)
            </span>
          </div>
        )}

        {/* Message metadata */}
        <div className={`flex flex-col gap-0.5 mt-1 px-1 ${isSender ? 'items-end' : 'items-start'}`}>
          <div className={`flex items-center gap-2 ${isSender ? 'flex-row-reverse' : ''}`}>
            {message.isEdited && (
              <span className="text-xs text-gray-400 italic">Edited</span>
            )}
            <span className="text-xs text-gray-500">
              {formatMessageDate(message.createdAt)}
            </span>
            
            {isSender && (
              <AnimatePresence mode="wait" initial={false}>
                <motion.div
                  key={`icon-${message.id}-${deliveryState}`}
                  initial={{ opacity: 0, y: 3, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -3, scale: 0.95 }}
                  transition={{ duration: 0.16, ease: 'easeOut' }}
                  className="flex items-center"
                >
                  {renderDeliveryIcon()}
                </motion.div>
              </AnimatePresence>
            )}
          </div>

          {isSender ? (
            <AnimatePresence mode="wait" initial={false}>
              <motion.span
                key={`label-${message.id}-${deliveryState}`}
                initial={{ opacity: 0, y: 2 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -2 }}
                transition={{ duration: 0.16, ease: 'easeOut' }}
                className="text-xs text-gray-400"
              >
                {renderDeliveryText()}
              </motion.span>
            </AnimatePresence>
          ) : null}

          {seenBySummary ? (
            <span className="text-xs text-blue-400" title={seenBySummary.title}>
              {seenBySummary.label}
            </span>
          ) : null}
          
          {/* Show read timestamp for sent messages */}
          {isSender && message.seen && message.readAt && (
            <span className="text-xs text-blue-400 font-medium">
              Read {formatDistanceToNow(new Date(message.readAt), { addSuffix: true })}
            </span>
          )}
        </div>

        {/* Quick action buttons (show on hover) */}
        <div className={`opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center gap-1 mt-1 ${
          isSender ? 'flex-row-reverse' : ''
        }`}>
          {!isEphemeral ? <MessageReactionPicker messageId={message.id} /> : null}
          
          <Button
            size="icon"
            variant="ghost"
            className="h-6 w-6 rounded-full hover:bg-gray-100"
            onClick={handleReply}
          >
            <Reply className="w-3 h-3 text-gray-500" />
          </Button>
          
          <div className="relative">
            <Button
              size="icon"
              variant="ghost"
              className="h-6 w-6 rounded-full hover:bg-gray-100"
              onClick={() => setShowActions(!showActions)}
            >
              <MoreHorizontal className="w-3 h-3 text-gray-500" />
            </Button>
            
            {showActions && (
              <div className="absolute bottom-full left-0 mb-2 bg-white border rounded-lg shadow-lg p-1 z-50 min-w-[120px]">
                <div className="flex flex-col">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="justify-start"
                    onClick={copyMessage}
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Copy
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="justify-start"
                    onClick={handleReply}
                  >
                    <Reply className="w-4 h-4 mr-2" />
                    Reply
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="justify-start"
                    onClick={handleForward}
                  >
                    <Forward className="w-4 h-4 mr-2" />
                    Forward
                  </Button>
                  {isSender && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="justify-start"
                      onClick={handleEdit}
                    >
                      <MoreHorizontal className="w-4 h-4 mr-2" />
                      Edit
                    </Button>
                  )}
                  {isSender && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="justify-start text-red-600 hover:text-red-700"
                      onClick={handleDelete}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Sender avatar (for sent messages) */}
      {isSender && (
        <div className="flex-shrink-0">
          <Avatar>
            <img
              src={message.senderPhoto || `https://api.dicebear.com/7.x/initials/svg?seed=${message.senderId}`}
              alt={message.senderName || 'You'}
              className="w-10 h-10 rounded-full object-cover"
            />
          </Avatar>
        </div>
      )}
    </div>
  );
}